import React from 'react';
import { Link } from 'react-router-dom';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip,
  ResponsiveContainer, ReferenceLine,
} from 'recharts';
import Icon from '../../../components/AppIcon';

const BenchmarkComparison = ({ benchmarkData }) => {
  if (!benchmarkData || !benchmarkData.available) {
    return (
      <div className="bg-white border border-slate-100 rounded-[2.5rem] p-10 shadow-sm flex items-center justify-center text-center">
        <div className="space-y-4">
          <Icon name="Target" size={32} className="text-slate-100 mx-auto" />
          <p className="text-xs font-black text-slate-400tracking-[0.2em] leading-relaxed">
            Configuration Benchmarking Data<br />will be released soon.
          </p>
          <Link to="/ranking-system" className="inline-flex items-center gap-1.5 text-[10px] font-black text-emerald-600 hover:text-emerald-500">
            <Icon name="Info" size={12} />
            Ranking method
          </Link>
        </div>
      </div>
    );
  }

  const { your_score, avg_score, max_score, percentile, top_percent, rank: reportRank, distribution, total_interviews } = benchmarkData;
  const topPercent = top_percent ?? Math.max(0, Math.round(100 - percentile));
  const rank = reportRank || Math.floor((total_interviews || 1) * (topPercent / 100)) + 1;
  const totalInterviews = total_interviews || 0;

  const scoreDiff = your_score - avg_score;
  const isAboveAvg = scoreDiff > 0;

  // Generate bell curve data
  const chartData = distribution ? distribution.map(d => ({
    score: parseInt(d.range.split('-')[1]) || parseInt(d.range.split('-')[0]),
    volume: d.count,
    label: d.range
  })) : Array.from({ length: 41 }, (_, i) => {
    const x = i * 2.5;
    const mean = 55;
    const sigma = 18;
    const value = Math.exp(-Math.pow(x - mean, 2) / (2 * Math.pow(sigma, 2)));
    return {
      score: x,
      volume: parseFloat((value * 100).toFixed(2)),
      label: `${x}%`
    };
  });

  return (
    <div className="bg-white border border-slate-100 rounded-[2.5rem] overflow-hidden shadow-sm flex flex-col lg:flex-row min-h-[280px]">
      {/* Left: Rank & Status */}
      <div className="lg:w-[260px] p-5 bg-slate-50/50 border-r border-slate-100 flex flex-col justify-center gap-5">
        <div className="relative group">
          <div className="absolute inset-0 bg-primary rounded-[2rem] blur-2xl opacity-20 group-hover:opacity-40 transition-opacity" />
          <div className="relative bg-primary rounded-[2rem] p-5 py-6 text-center shadow-lg shadow-primary/20 transition-all duration-300">
            <Link
              to="/ranking-system"
              className="absolute right-4 top-4 flex h-6 w-6 items-center justify-center rounded-full bg-white/15 text-white transition hover:bg-white/25"
              title="How rank is calculated"
              aria-label="How rank is calculated"
            >
              <Icon name="Info" size={13} />
            </Link>
            <p className="text-[9px] font-black text-white/85 dark:text-black/75tracking-[0.2em] mb-3">Report Rank</p>
            <h3 className="text-2xl font-black text-white dark:text-black tracking-tighter">
              #{rank} <span className="text-lg text-white/75 dark:text-black/60 font-bold">/ {totalInterviews.toLocaleString()}</span>
            </h3>
            <p className="mt-3 text-[9px] font-black text-white/70 dark:text-black/60">Top {topPercent}% in this setup</p>
          </div>
        </div>
      </div>

      {/* Center: Distribution Curve */}
      <div className="flex-1 p-5 flex flex-col">
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h4 className="text-[9px] font-black text-slate-400tracking-[0.2em] mb-1">Same Configuration Distribution</h4>
            <p className="text-xs font-black text-slate-600">Candidate density for this interview setup</p>
          </div>
          <Link
            to="/ranking-system"
            className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 text-[10px] font-black text-slate-500 transition hover:border-emerald-200 hover:bg-emerald-50 hover:text-emerald-600"
          >
            <Icon name="Info" size={12} />
            Method
          </Link>
        </div>

        <div className="flex-1 min-h-[160px] relative">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 20, right: 30, left: 30, bottom: 0 }}>
              <defs>
                <linearGradient id="curveGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10E58F" stopOpacity={0.15} />
                  <stop offset="95%" stopColor="#10E58F" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="score" hide />
              <YAxis hide />
              <Tooltip
                content={({ active, payload }) => {
                  if (active && payload && payload.length) {
                    return (
                      <div className="bg-slate-900 text-white px-3 py-2 rounded-xl shadow-2xl border border-white/10">
                        <p className="text-[9px] font-black text-slate-400tracking-widest mb-1">{payload[0].payload.label}</p>
                        <p className="text-xs font-black">{payload[0].payload.volume} Candidates</p>
                      </div>
                    );
                  }
                  return null;
                }}
              />
              <Area
                type="monotone"
                dataKey="volume"
                stroke="#10E58F"
                strokeWidth={3}
                fillOpacity={1}
                fill="url(#curveGradient)"
                isAnimationActive={true}
              />

              {/* Global Average Line */}
              <ReferenceLine
                x={avg_score}
                stroke="#94A3B8"
                strokeWidth={1}
                strokeDasharray="4 4"
                label={({ viewBox }) => (
                  <text
                    x={viewBox.x}
                    y={viewBox.y - 12}
                    textAnchor="middle"
                    fill="#94A3B8"
                    fontSize="9"
                    fontWeight="800"
                    className="tracking-wider uppercase"
                  >
                    Average
                  </text>
                )}
              />

              {/* YOUR SCORE Indicator */}
              <ReferenceLine
                x={your_score}
                stroke="#10E58F"
                strokeWidth={2}
                label={({ viewBox }) => (
                  <text
                    x={viewBox.x}
                    y={viewBox.y - 12}
                    textAnchor="middle"
                    fill="#10E58F"
                    fontSize="9"
                    fontWeight="900"
                    className="tracking-widestfilter drop-shadow-sm"
                  >
                    You
                  </text>
                )}
              />
            </AreaChart>
          </ResponsiveContainer>

          <div className="absolute bottom-0 left-0 right-0 flex justify-between px-6 text-[9px] font-black text-slate-300tracking-[0.3em]">
            <span>Low</span>
            <span className="text-slate-400">Competitive Edge</span>
            <span>High</span>
          </div>
        </div>
      </div>

      {/* Right: Sidebar Stats */}
      <div className="lg:w-[220px] p-5 flex flex-col justify-center bg-slate-50/30 border-l border-slate-100">
        <div className="space-y-5">
          <div>
            <p className="text-[9px] font-black text-emerald-500tracking-[0.2em] mb-1">Your Score</p>
            <h3 className="text-2xl font-black text-slate-900 leading-none">{Math.round(your_score)}</h3>
          </div>

          <div>
            <p className="text-[9px] font-black text-slate-400tracking-[0.2em] mb-1">Cohort Avg</p>
            <h3 className="text-2xl font-black text-slate-900 leading-none">{Math.round(avg_score)}</h3>
          </div>

          <div>
            <p className="text-[9px] font-black text-slate-400tracking-[0.2em] mb-1">Peak Perf</p>
            <h3 className="text-2xl font-black text-slate-900 leading-none">{Math.round(max_score)}</h3>
          </div>
        </div>

        <div className="mt-8 pt-6 border-t border-slate-200">
          <p className="text-[9px] font-black text-slate-400tracking-[0.4em] mb-2">Competitive Edge</p>
          <div className="flex items-center gap-2">
            <span className={`text-xl font-black ${isAboveAvg ? 'text-emerald-500' : 'text-rose-500'}`}>
              {isAboveAvg ? '+' : ''}{Math.round(scoreDiff)}
            </span>
            <Icon name={isAboveAvg ? 'TrendingUp' : 'TrendingDown'} size={18} className={isAboveAvg ? 'text-emerald-500' : 'text-rose-500'} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default BenchmarkComparison;
